import {css} from '@emotion/css';
import {
  getMarketRemoovFacebookLink,
  getMarketRemoovInstagramLink,
  getMarketRemoovTwitterLink,
  getMarketRemoovYelpLink,
  getMarketTelephone,
} from '@remoov/functional';
import {EmailSocialMedia, MediaMatch, theme} from '@remoov/remoov-components';
import React, {AnchorHTMLAttributes, HTMLAttributes, ImgHTMLAttributes} from 'react';
import awards from './awards.svg';
import remoovLogo from './logoblue.svg';
import paypal from './paypal.png';
import yelp from './yelpbig.svg';

export const Footer = ({market}: {market: Maybe<string>}) => (
  <Container>
    <SubContainer>
      <Row1>
        <Logo src={remoovLogo} alt='remoov logo' />
        <MediasContainer>
          <SocialMediasContainer>
            <EmailSocialMedia
              socialmedia='Yelp'
              url={getMarketRemoovYelpLink(market)}
              color='blue'
              size='big'
            />
            <EmailSocialMedia
              socialmedia='Instagram'
              url={getMarketRemoovInstagramLink(market)}
              color='blue'
              size='big'
            />
            <EmailSocialMedia
              socialmedia='Twitter'
              url={getMarketRemoovTwitterLink(market)}
              color='blue'
              size='big'
            />
            <EmailSocialMedia
              socialmedia='Facebook'
              url={getMarketRemoovFacebookLink(market)}
              color='blue'
              size='big'
            />
          </SocialMediasContainer>
          <MediaMatch greaterThan='medium'>
            <LastLogos>
              <Logo src={paypal} alt='paypal logo' />
              <Logo src={yelp} alt='yelp logo' />
              <Logo src={awards} alt='paypal logo' />
            </LastLogos>
          </MediaMatch>
        </MediasContainer>
      </Row1>
      <LinksRow>
        <LinkText>{getMarketTelephone(market)}</LinkText>
        <LinkText href='https://remoovit.com/service'>Services</LinkText>
        <LinkText href='https://remoovit.com/how'>How it works</LinkText>
        <LinkText href='https://remoovit.com/pricing'>Pricing</LinkText>
        <LinkText href='https://remoovit.com/faq'>FAQ</LinkText>
        <LinkText href='https://remoovit.com/about'>About</LinkText>
        <LinkText href='https://remoovit.com/service_area'>Service Area</LinkText>
        <LinkText href='https://blog.remoovit.com/'>Blog</LinkText>
      </LinksRow>
      <Row2>
        <MediaMatch lessThan='medium' style={{width: '100%'}}>
          <LastLogos>
            <Logo src={paypal} alt='paypal logo' />
            <Logo src={yelp} alt='yelp logo' />
            <Logo src={awards} alt='paypal logo' />
          </LastLogos>
        </MediaMatch>
        <FooterText>
          © Copyright {new Date().getFullYear()}. All Rights Reserved. Remoov, Inc. Terms of service
          | Privacy Policy
        </FooterText>
      </Row2>
    </SubContainer>
  </Container>
);
const Container = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '100%',
      background: theme.colors.lightBlue,
      height: '300px',
      padding: `${theme.spacings.large} 0`,
      display: 'flex',
      justifyContent: 'center',
      [theme.mediaMaxWidth.medium]: {
        padding: `${theme.spacings.large} ${theme.spacings.small}`,
        height: '570px',
      },
    })}
    {...args}
  />
);
const SubContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '100%',
      maxWidth: '1200px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      padding: `0 ${theme.spacings.xxsmall}`,
    })}
    {...args}
  />
);
const Row1 = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '100%',
      display: 'grid',
      gridTemplateColumns: '1fr 2fr',
      alignItems: 'center',
      '@media (max-width:920px)': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        img: {margin: '12px 0'},
        div: {margin: '12px 0'},
      },
      [theme.mediaMaxWidth.medium]: {img: {margin: '24px 0'}, div: {margin: '24px 0'}},
    })}
    {...args}
  />
);
const MediasContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      '@media (max-width:920px)': {padding: 0},
    })}
    {...args}
  />
);
const Row2 = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      [theme.mediaMaxWidth.medium]: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        div: {margin: '24px 0'},
      },
      [theme.mediaMaxWidth.small]: {justifyContent: 'space-between'},
    })}
    {...args}
  />
);
const LinksRow = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      [theme.mediaMaxWidth.medium]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridTemplateRows: '1fr 1fr 1fr',
        gap: theme.spacings.small,
        placeItems: 'center',
        whiteSpace: 'nowrap',
      },
      [theme.mediaMaxWidth.small]: {
        placeItems: 'unset',
      },
    })}
    {...args}
  />
);
const Logo = (args: ImgHTMLAttributes<HTMLImageElement>) => (
  <img
    className={css({
      height: '32px',
      [theme.mediaMaxWidth.small]: {height: '28px'},
    })}
    {...args}
  />
);
export const SocialMediasContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      div: {margin: `0 ${theme.spacings.small}`},
      '@media (max-width:920px)': {
        justifyContent: 'space-between',
        div: {margin: `0 ${theme.spacings.large} 0 0`},
      },
      [theme.mediaMaxWidth.medium]: {width: '100%', div: {margin: 0}},
    })}
    {...args}
  />
);
const LastLogos = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      svg: {height: '56px', margin: `0 ${theme.spacings.xsmall}`},
      img: {height: '56px', margin: `0 ${theme.spacings.xsmall}`},
      '@media (max-width:920px)': {svg: {margin: '0 20px'}, img: {margin: '0 20px'}},
      [theme.mediaMaxWidth.medium]: {width: '100%', margin: 0, justifyContent: 'space-between'},
    })}
    {...args}
  />
);
const LinkText = (args: AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a
    className={css({
      color: theme.colors.darkBlue,
      fontFamily: theme.font.family,
      fontSize: theme.font.sizes.small,
      fontWeight: theme.font.bold,
      cursor: 'pointer',
      textDecoration: 'none',
    })}
    {...args}
  />
);
const FooterText = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      color: theme.colors.darkBlue,
      fontFamily: theme.font.family,
      fontWeight: theme.font.light,
      fontSize: theme.font.sizes.xsmall,
      maxWidth: '284px',
      [theme.mediaMaxWidth.medium]: {width: '100%', alignSelf: 'start'},
    })}
    {...args}
  />
);
